import React, { useState, useEffect, useCallback } from "react";
import "@/App.css";
import Lenis from "lenis";
import { AnimatePresence, motion } from "framer-motion";
import { Toaster, toast } from "sonner";

import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { CartDrawer } from "@/components/CartDrawer";
import { QuickView } from "@/components/QuickView";
import { Home } from "@/pages/Home";
import { Story } from "@/pages/Story";
import { Shop } from "@/pages/Shop";
import { CareJournal } from "@/pages/CareJournal";
import { Contact } from "@/pages/Contact";

const CART_KEY = "kalorence_cart";

function App() {
  const [activeTab, setActiveTab] = useState("home");
  const [cart, setCart] = useState(() => {
    try {
      const saved = localStorage.getItem(CART_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [cartOpen, setCartOpen] = useState(false);
  const [quickViewProduct, setQuickViewProduct] = useState(null);
  const [shopCategory, setShopCategory] = useState("all");

  // Smooth scrolling
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);

  // Persist cart
  useEffect(() => {
    localStorage.setItem(CART_KEY, JSON.stringify(cart));
  }, [cart]);

  const addToCart = useCallback((product) => {
    setCart((prev) => {
      const existing = prev.find((i) => i.id === product.id);
      if (existing) {
        return prev.map((i) =>
          i.id === product.id ? { ...i, quantity: i.quantity + 1 } : i
        );
      }
      return [
        ...prev,
        {
          id: product.id,
          title: product.title,
          price: product.price,
          category: product.category,
          image: product.image,
          quantity: 1,
        },
      ];
    });
    toast.success("Added to your basket", {
      description: product.title,
    });
  }, []);

  const updateQuantity = useCallback((id, quantity) => {
    if (quantity < 1) {
      setCart((prev) => prev.filter((i) => i.id !== id));
      return;
    }
    setCart((prev) =>
      prev.map((i) => (i.id === id ? { ...i, quantity } : i))
    );
  }, []);

  const removeFromCart = useCallback((id) => {
    setCart((prev) => prev.filter((i) => i.id !== id));
  }, []);

  const clearCart = useCallback(() => setCart([]), []);

  const goShopCategory = useCallback((category) => {
    setShopCategory(category);
    setActiveTab("shop");
    window.scrollTo({ top: 0 });
  }, []);

  const cartCount = cart.reduce((acc, i) => acc + i.quantity, 0);

  const renderPage = () => {
    switch (activeTab) {
      case "story":
        return <Story />;
      case "shop":
        return (
          <Shop
            onAdd={addToCart}
            onQuickView={setQuickViewProduct}
            initialCategory={shopCategory}
          />
        );
      case "care":
        return <CareJournal />;
      case "contact":
        return <Contact />;
      default:
        return (
          <Home
            onAdd={addToCart}
            onQuickView={setQuickViewProduct}
            setActiveTab={setActiveTab}
            goShopCategory={goShopCategory}
          />
        );
    }
  };

  return (
    <div className="App bg-[#F7F5F0] min-h-screen">
      <div className="grain-overlay" />
      <Toaster position="bottom-right" theme="dark" richColors />

      <Navbar
        activeTab={activeTab}
        setActiveTab={(tab) => {
          if (tab === "shop") setShopCategory("all");
          setActiveTab(tab);
        }}
        cartCount={cartCount}
        toggleCart={() => setCartOpen(true)}
      />

      <AnimatePresence mode="wait">
        <motion.main
          key={activeTab}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
        >
          {renderPage()}
        </motion.main>
      </AnimatePresence>

      <Footer setActiveTab={setActiveTab} />

      <CartDrawer
        isOpen={cartOpen}
        onClose={() => setCartOpen(false)}
        cart={cart}
        updateQuantity={updateQuantity}
        removeFromCart={removeFromCart}
        clearCart={clearCart}
      />

      <QuickView
        product={quickViewProduct}
        onClose={() => setQuickViewProduct(null)}
        onAdd={addToCart}
      />
    </div>
  );
}

export default App;
